import { forwardRef } from "react";

import { StackProps, Stack, useTheme } from "@mui/material";
import { getDollarValue, AvailablePaymentCurrency, formatNarrowCurrencyAmount } from "@simplyk/common";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

type FormPaymentOrderItemProps = Omit<StackProps, "children"> & {
  label: string | React.ReactNode;
  price: number | React.ReactNode;
  currency: AvailablePaymentCurrency;
};

export const FormPaymentOrderItem = forwardRef<HTMLDivElement, FormPaymentOrderItemProps>(
  ({ label, price, currency, ...rest }, ref) => {
    const theme = useTheme();
    const { isoLocale } = useLocaleContext();

    if (!theme.palette.forms || !theme.palette.formsComponents?.paymentSummary) {
      return <InvalidThemeErrorMessage />;
    }

    const renderedPrice =
      typeof price === "number" ? (
        <FormTypography variant="body2" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          {formatNarrowCurrencyAmount(getDollarValue(price), { withCents: true, locale: isoLocale, currency })}
        </FormTypography>
      ) : (
        price
      );

    return (
      <Stack
        ref={ref}
        direction="row"
        justifyContent="space-between"
        gap={2}
        data-test="order-ticket-information"
        sx={{ minWidth: 0 }}
        {...rest}
      >
        <FormTypography variant="body2" sx={{ minWidth: 0, flex: "1 1 auto" }}>
          {label}
        </FormTypography>
        {renderedPrice}
      </Stack>
    );
  }
);

FormPaymentOrderItem.displayName = "FormPaymentOrderItem";
